import React from 'react';
import { User } from '../../types';

interface ProfileSectionProps {
  user: User;
  showChangePassword: boolean;
  setShowChangePassword: (show: boolean) => void;
  currentPassword: string;
  setCurrentPassword: (value: string) => void;
  newPassword: string;
  setNewPassword: (value: string) => void;
  confirmPassword: string;
  setConfirmPassword: (value: string) => void;
  isChangingPassword: boolean;
  onChangePassword: (e: React.FormEvent) => void;
  /**
   * Which part to render. The Settings contents page (Almanac S6) opens
   * "Name and email" (`identity`) and "Sign-in" (`sign-in`) as separate rows;
   * `all` is the whole section, as before.
   */
  part?: 'all' | 'identity' | 'sign-in';
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  user,
  showChangePassword,
  setShowChangePassword,
  currentPassword,
  setCurrentPassword,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  isChangingPassword,
  onChangePassword,
  part = 'all',
}) => (
  <section className="settings-section">
    {part === 'all' && (
      <>
        <h2>Profile</h2>
        <p className="section-hint">Your account information</p>
      </>
    )}

    <div className="profile-info">
      {part !== 'sign-in' && (
        <>
          <div className="profile-row">
            <span className="profile-label">Email</span>
            <span className="profile-value">{user.email}</span>
          </div>
          <div className="profile-row">
            <span className="profile-label">Name</span>
            <span className="profile-value">
              {user.first_name || user.last_name
                ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                : 'Not set'}
            </span>
          </div>
        </>
      )}
      {part !== 'identity' && (
        <div className="profile-row">
          <span className="profile-label">Connected accounts</span>
          <span className="profile-value profile-accounts">
            {user.has_google_token && (
              <span className="account-badge google">Google</span>
            )}
            {user.has_microsoft_token && (
              <span className="account-badge microsoft">Microsoft</span>
            )}
            {!user.has_google_token && !user.has_microsoft_token && (
              <span className="account-badge email">Email only</span>
            )}
          </span>
        </div>
      )}
    </div>

    {part !== 'identity' && user.has_password && (
      <div className="password-section">
        {!showChangePassword ? (
          <button
            type="button"
            onClick={() => setShowChangePassword(true)}
            className="btn-link-block"
          >
            Change Password
          </button>
        ) : (
          <form onSubmit={onChangePassword} className="change-password-form">
            <h3>Change Password</h3>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Current password"
              aria-label="Current password"
              className="input-field"
              autoComplete="current-password"
            />
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              aria-label="New password"
              className="input-field"
              autoComplete="new-password"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              aria-label="Confirm new password"
              className="input-field"
              autoComplete="new-password"
            />
            <div className="form-buttons">
              <button type="submit" disabled={isChangingPassword} className="btn-primary">
                {isChangingPassword ? 'Changing...' : 'Change Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowChangePassword(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="btn-link"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    )}
  </section>
);

export default ProfileSection;
